import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { finalize } from "rxjs";
import { ApiServiceService } from "../../../Services/api-service.service";

type VerificationState = "found" | "not-found" | "error";

@Component({
  selector: "app-taas-public-landing",
  templateUrl: "./taas-landing.component.html",
  styleUrls: ["./taas-landing.component.css"],
})
export class TaasLandingComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild("verificationSection") verificationSection!: ElementRef<HTMLElement>;
  @ViewChild("taasIdInput") taasIdInput!: ElementRef<HTMLInputElement>;
  @ViewChild("verificationModal") verificationModal!: TemplateRef<any>;

  verificationForm: FormGroup;
  isVerifying = false;
  verificationState: VerificationState = "found";
  verificationResult: any = {};
  modalRef?: BsModalRef;
  private revealObserver?: IntersectionObserver;

  capabilities = [
    {
      title: "Seamless Digital Onboarding",
      text: "Submit your application, input your professional background, and upload all supporting documents from one guided experience.",
    },
    {
      title: "Real-Time Tracker & Notifications",
      text: "Stay informed with email and SMS updates on your application lifecycle from initial ingestion through full administrative screening.",
    },
    {
      title: "Automated Accreditation Credentials",
      text: "Upon approval, the system assigns your verified accreditation number and prepares your official certificate for digital download.",
    },
    {
      title: "Public Validation & Placement",
      text: "Approved agents are added to the public register so taxpayers can confirm authentic credentials in seconds.",
    },
  ];

  requirements = [
    {
      title: "Accreditation",
      text: "Current membership with CITN, ICAN, or ANAN.",
    },
    {
      title: "Identification",
      text: "Valid NIN, voter's card, or international passport.",
    },
    {
      title: "Tax Verification",
      text: "Active ASIN/TIN and a valid Tax Clearance Certificate.",
    },
    {
      title: "Declarations",
      text: "Clean legal record and compliance with the platform's terms of use.",
    },
  ];

  steps = [
    {
      title: "Account Creation",
      text: "Create your unique practitioner profile using your professional details and valid tax identification number (ASIN).",
      iconPath:
        "M31.7812 34.334V31.834C31.7812 30.5079 31.2545 29.2361 30.3168 28.2985C29.3791 27.3608 28.1073 26.834 26.7812 26.834H19.2812C17.9552 26.834 16.6834 27.3608 15.7457 28.2985C14.808 29.2361 14.2812 30.5079 14.2812 31.834V34.334M28.0312 16.834C28.0312 19.5954 25.7927 21.834 23.0312 21.834C20.2698 21.834 18.0312 19.5954 18.0312 16.834C18.0312 14.0726 20.2698 11.834 23.0312 11.834C25.7927 11.834 28.0312 14.0726 28.0312 16.834Z",
    },
    {
      title: "Document Upload",
      text: "Complete the online questionnaire and attach clear PDF copies of your licenses, ID, and Tax Clearance Certificate.",
      iconPath:
        "M23 11.5684V26.5684M16.75 17.8184L23 11.5684L29.25 17.8184M34.25 26.5684V31.5684C34.25 32.2314 33.9866 32.8673 33.5178 33.3361C33.0489 33.805 32.413 34.0684 31.75 34.0684H14.25C13.587 34.0684 12.9511 33.805 12.4822 33.3361C12.0134 32.8673 11.75 32.2314 11.75 31.5684V26.5684",
    },
    {
      title: "Administrative Evaluation",
      text: "The AIRS compliance team checks your application against backend registries for exact alignment and validity.",
      iconPath:
        "M15.97 31.4554C16.5034 30.0207 17.463 28.7835 18.72 27.91C19.977 27.0365 21.4712 26.5685 23.0019 26.5689C24.5326 26.5693 26.0266 27.0381 27.2831 27.9122C28.5396 28.7864 29.4986 30.0241 30.0312 31.4592M33 24.0679C33 30.3179 28.625 33.4429 23.425 35.2554C23.1527 35.3477 22.8569 35.3433 22.5875 35.2429C17.375 33.4429 13 30.3179 13 24.0679V15.3179C13 14.9864 13.1317 14.6685 13.3661 14.4341C13.6005 14.1996 13.9185 14.0679 14.25 14.0679C16.75 14.0679 19.875 12.5679 22.05 10.6679C22.3148 10.4417 22.6517 10.3174 23 10.3174C23.3483 10.3174 23.6852 10.4417 23.95 10.6679C26.1375 12.5804 29.25 14.0679 31.75 14.0679C32.0815 14.0679 32.3995 14.1996 32.6339 14.4341C32.8683 14.6685 33 14.9864 33 15.3179V24.0679ZM28 21.5679C28 24.3294 25.7614 26.5679 23 26.5679C20.2386 26.5679 18 24.3294 18 21.5679C18 18.8065 20.2386 16.5679 23 16.5679C25.7614 16.5679 28 18.8065 28 21.5679Z",
    },
    {
      title: "Certification & Launch",
      text: "Receive your accreditation number, get indexed on the active registry, and download your credential kit.",
      iconPath:
        "M27.3462 23.9309L29.24 34.5884C29.2612 34.7139 29.2436 34.8428 29.1895 34.958C29.1355 35.0733 29.0475 35.1692 28.9374 35.2331C28.8273 35.297 28.7004 35.3257 28.5735 35.3155C28.4466 35.3053 28.3259 35.2565 28.2275 35.1759L23.7525 31.8171C23.5365 31.6557 23.274 31.5685 23.0044 31.5685C22.7347 31.5685 22.4723 31.6557 22.2562 31.8171L17.7737 35.1746C17.6754 35.2552 17.5548 35.3038 17.4281 35.314C17.3014 35.3243 17.1746 35.2956 17.0646 35.2319C16.9546 35.1682 16.8666 35.0725 16.8124 34.9575C16.7582 34.8425 16.7404 34.7138 16.7612 34.5884L18.6537 23.9309M30.5 17.8184C30.5 21.9605 27.1421 25.3184 23 25.3184C18.8579 25.3184 15.5 21.9605 15.5 17.8184C15.5 13.6762 18.8579 10.3184 23 10.3184C27.1421 10.3184 30.5 13.6762 30.5 17.8184Z",
    },
  ];

  constructor(
    private fb: FormBuilder,
    private api: ApiServiceService,
    private modalService: BsModalService,
    private router: Router,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.verificationForm = this.fb.group({
      id: ["", [Validators.required]],
    });
  }

  ngAfterViewInit(): void {
    const revealItems = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>("[data-reveal]"));

    if (!("IntersectionObserver" in window)) {
      revealItems.forEach((item) => item.classList.add("is-visible"));
      return;
    }

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          entry.target.classList.add("is-visible");
          this.revealObserver?.unobserve(entry.target);
        });
      },
      {
        rootMargin: "0px 0px -12% 0px",
        threshold: 0.16,
      },
    );

    revealItems.forEach((item) => this.revealObserver?.observe(item));
  }

  ngOnDestroy(): void {
    this.revealObserver?.disconnect();
  }

  startApplication(): void {
    this.router.navigate(["/auth/login"], {
      queryParams: { returnUrl: "/user/taas" },
    });
  }

  scrollToVerification(): void {
    this.verificationSection?.nativeElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    setTimeout(() => {
      this.taasIdInput?.nativeElement.focus();
    }, 450);
  }

  verifyAgent(): void {
    if (this.verificationForm.invalid || this.isVerifying) {
      this.verificationForm.markAllAsTouched();
      return;
    }

    const dto = {
      id: String(this.verificationForm.get("id")?.value || "").trim(),
      type: "tass-assessmentcert",
    };

    this.isVerifying = true;
    this.api
      .post("/assessments/document/verifications", dto)
      .pipe(finalize(() => (this.isVerifying = false)))
      .subscribe({
        next: (response: any) => {
          const data = response?.data || {};
          this.verificationResult = data;
          this.verificationState = data?.message === "Found" ? "found" : "not-found";
          this.openVerificationModal();
        },
        error: () => {
          this.verificationResult = {};
          this.verificationState = "error";
          this.openVerificationModal();
        },
      });
  }

  closeVerificationModal(): void {
    this.modalRef?.hide();
  }

  private openVerificationModal(): void {
    this.modalRef = this.modalService.show(this.verificationModal, {
      class: this.verificationState === "found"
        ? "modal-lg modal-dialog-centered"
        : "modal-md modal-dialog-centered",
      backdrop: true,
      ignoreBackdropClick: false,
    });
  }
}
